import { useState, useEffect } from "react";
import {
  Container,
  Table,
  Center,
  Loader,
  Button,
  Group,
  ActionIcon,
  Stack,
  Pagination,
} from "@mantine/core";
import { Link, useLocation } from "wouter";
import { deleteUser, getUsers, sendUserInvitation } from "../api";
import {
  IconCheck,
  IconPencil,
  IconRefresh,
  IconTrash,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import openDeleteModal from "../_Shared/ConfirmDelete";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import ReactTimeago from "../_Shared/Timeago";
import { useStore } from "../store";

export default function UserList() {
  const [, setLocation] = useLocation();
  const [users, setUsers] = useState(null);
  const { t } = useTranslation("Users");
  const profile = useStore((state) => state.profile);

  const [activePage, setActivePage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const pageSize = 50;

  useEffect(() => {
    setIsLoading(true);
    getUsers(activePage, pageSize)
      .then((data) => {
        setUsers(data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [activePage]);

  if (!users)
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );

  return (
    <Container px={0}>
      <Stack>
        <Group justify="flex-end">
          <Button
            component={Link}
            to="/users/add"
            my="sm"
            variant="outline"
            leftSection={<IconUserPlus />}
          >
            {t("add_user")}
          </Button>
        </Group>

        <Pagination
          hideWithOnePage={false}
          total={Math.max(1, Math.ceil(users.totalCount / pageSize))}
          value={activePage}
          onChange={setActivePage}
        />
        {isLoading ? <Loader size="sm" /> : null}

        <Table stickyHeader stickyHeaderOffset={60}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("email")}</Table.Th>
              <Table.Th visibleFrom="sm">{t("first_name")}</Table.Th>
              <Table.Th visibleFrom="xs">{t("last_name")}</Table.Th>
              <Table.Th visibleFrom="xs">{t("role")}</Table.Th>
              <Table.Th visibleFrom="sm">{t("created")}</Table.Th>
              <Table.Th visibleFrom="xs">{t("is_active")}</Table.Th>
              <Table.Th aria-label={t("actions", "Actions")} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {users.data.map((p) => (
              <Table.Tr
                // onClick={() => setLocation(`/users/details/${p.id}`)}
                key={p.id}
              >
                <Table.Td>{p.emailAddress}</Table.Td>
                <Table.Td visibleFrom="sm">{p.firstName}</Table.Td>
                <Table.Td visibleFrom="xs">{p.lastName}</Table.Td>
                <Table.Td visibleFrom="xs">{p.role ?? p.userRole}</Table.Td>
                <Table.Td visibleFrom="sm">
                  <ReactTimeago date={p.created} />
                </Table.Td>
                {/* <Table.Td>{p.lastActivity}</Table.Td> */}
                <Table.Td visibleFrom="xs">
                  {p.isActive ? <IconCheck /> : <IconX />}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <ActionIcon
                      size="sm"
                      variant="outline"
                      component={p.id !== profile?.id ? Link : "span"}
                      disabled={p.id === profile?.id}
                      to={`/users/edit/${p.id}`}
                    >
                      <IconPencil size={14} />
                    </ActionIcon>
                    <ActionIcon
                      size="sm"
                      variant="outline"
                      color="red"
                      onClick={async () => {
                        openDeleteModal(() => {
                          deleteUser(p.id)
                            .then(() => {
                              setUsers({
                                ...users,
                                totalCount: users.totalCount - 1,
                                data: users.data.filter((u) => u.id !== p.id),
                              });
                              notifications.show({
                                title: t("user.deleted", "User Deleted"),
                                message: t(
                                  "user.deleted_message",
                                  "The user has been removed from the system"
                                ),
                              });
                            })
                            .catch((err) => {
                              notifications.show({
                                title: t("generic.error"),
                                message: "" + err,
                                color: "red",
                              });
                            });
                        });
                      }}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                    <ActionIcon
                      size="sm"
                      disabled={p.isActive}
                      onClick={async () => {
                        await sendUserInvitation(p.id);
                        notifications.show({
                          color: "green",
                          autoClose: 3000,
                          title: t("invitation_sent"),
                        });
                        setLocation("/users");
                      }}
                    >
                      <IconRefresh size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Container>
  );
}
